import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Tenant } from '@shared/types';
import { formatDateTime } from '@shared/format';
import { useAuthStore } from '../app/authStore';
import { ApiError, api, describeError } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { Button, Card, CardHeader, PageHeader, Skeleton, StatBlock } from '../components/ui';
import { Field, Input, Notice, Select } from '../components/form';
import { ConfirmDialog, Modal, useToast } from '../components/feedback';

interface OrgResponse {
  tenant: Tenant;
  stats: {
    memberCount: number;
    pendingInvitationCount: number;
    clientCount: number;
    ownerEmail: string | null;
  };
}

/** 组织资料。改标识（slug）等于改对外可见的组织标识，因此单独给一行说明。 */
export function OrgPage() {
  const can = useAuthStore((state) => state.can);
  const session = useAuthStore((state) => state.session);
  const refresh = useAuthStore((state) => state.refresh);
  const toast = useToast();
  const navigate = useNavigate();

  const org = useAsync<OrgResponse>(() => api.get('/api/org'), []);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [kind, setKind] = useState<'personal' | 'enterprise'>('enterprise');
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [upgradeCode, setUpgradeCode] = useState('');
  const [upgradeError, setUpgradeError] = useState<string | null>(null);

  const manage = can('org.manage');
  const currentKind = org.data?.tenant.kind ?? 'personal';
  // 企业组织不能降级为个人：界面先禁用，Worker 还会再拒绝一次。
  const kindLocked = currentKind === 'enterprise';
  const totpEnabled = session?.user.totpEnabled === true;

  // 数据到位后再灌进表单，避免把用户正在输入的内容覆盖掉。
  useEffect(() => {
    if (!org.data) return;
    setName(org.data.tenant.name);
    setSlug(org.data.tenant.slug);
    setKind(org.data.tenant.kind);
  }, [org.data]);

  async function save(totpCode?: string) {
    setBusy(true);
    setErrors({});
    try {
      const result = await api.patch<OrgResponse>('/api/org', {
        name: name.trim(),
        slug: slug.trim(),
        kind,
        ...(totpCode === undefined ? {} : { totpCode }),
      });
      org.setData(result);
      // 顶栏显示的组织名要跟着变。
      await refresh();
      if (totpCode !== undefined) {
        setUpgradeOpen(false);
        setUpgradeCode('');
        toast.push('已升级为企业组织，请提交企业资质完成认证', 'ok');
        navigate('/org/verification');
      } else {
        toast.push('组织资料已保存', 'ok');
      }
    } catch (error) {
      if (totpCode !== undefined) {
        if (error instanceof ApiError) {
          setUpgradeError(error.message);
          setUpgradeCode('');
        } else {
          setUpgradeError(describeError(error));
        }
      } else if (error instanceof ApiError) {
        setErrors(error.fieldErrors);
        toast.push(error.message, 'danger');
      } else {
        toast.push(describeError(error), 'danger');
      }
    } finally {
      setBusy(false);
    }
  }

  /** 类型变更不走普通保存：先打开警告与两步验证确认框。 */
  function requestSave() {
    if (!org.data) return;
    if (kind === org.data.tenant.kind) {
      void save();
      return;
    }
    if (org.data.tenant.kind === 'enterprise') {
      toast.push('企业组织不能变更为个人组织', 'danger');
      return;
    }
    setErrors({});
    setUpgradeError(null);
    setUpgradeCode('');
    setUpgradeOpen(true);
  }

  return (
    <div className="space-y-5">
      <PageHeader title="组织资料" description="组织是这套控制台的租户边界：成员、接入应用与操作记录都归属其中。" />

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <Card>
          <CardHeader title="基本信息" />
          {org.loading ? (
            <Skeleton className="h-40 w-full" />
          ) : (
            <form
              className="space-y-4"
              onSubmit={(event) => {
                event.preventDefault();
                requestSave();
              }}
            >
              <Field label="组织名称" htmlFor="org-name" required error={errors.name}>
                <Input id="org-name" value={name} onChange={setName} disabled={!manage} required />
              </Field>

              <Field
                label="组织标识"
                htmlFor="org-slug"
                required
                error={errors.slug}
                hint="2–40 位小写字母、数字或连字符。用作对外可见的组织标识。"
              >
                <Input id="org-slug" value={slug} onChange={setSlug} disabled={!manage} required />
              </Field>

              <Field
                label="组织类型"
                hint={
                  kindLocked
                    ? '企业组织不能变更为个人组织。如需以个人身份使用，请新建个人账号。'
                    : '个人空间可升级为企业组织；升级后需要提交企业资质重新审核，且无法再改回个人。'
                }
              >
                <Select
                  value={kind}
                  disabled={!manage || kindLocked}
                  options={[
                    { value: 'enterprise', label: '企业' },
                    { value: 'personal', label: '个人' },
                  ]}
                  onChange={setKind}
                />
              </Field>

              {manage ? (
                <div className="flex items-center gap-2">
                  <Button type="submit" variant="primary" loading={busy}>
                    保存
                  </Button>
                  <Button
                    onClick={() => {
                      if (org.data) {
                        setName(org.data.tenant.name);
                        setSlug(org.data.tenant.slug);
                        setKind(org.data.tenant.kind);
                      }
                    }}
                    disabled={busy}
                  >
                    撤销改动
                  </Button>
                </div>
              ) : (
                <Notice tone="info">当前角色只能查看组织资料，不能修改。</Notice>
              )}
            </form>
          )}
        </Card>

        <div className="space-y-5">
          <Card>
            <CardHeader title="规模" />
            <div className="space-y-3">
              <StatBlock label="成员" value={org.data?.stats.memberCount ?? '—'} />
              <StatBlock label="接入应用" value={org.data?.stats.clientCount ?? '—'} />
              <StatBlock label="待接受邀请" value={org.data?.stats.pendingInvitationCount ?? '—'} />
            </div>
          </Card>

          <Card>
            <CardHeader title="归属" />
            <dl className="space-y-2 text-[13px]">
              <div className="flex justify-between gap-3">
                <dt className="text-fg-muted">所有者</dt>
                <dd className="truncate text-fg">{org.data?.stats.ownerEmail ?? '—'}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-fg-muted">创建时间</dt>
                <dd className="text-fg">{formatDateTime(org.data?.tenant.createdAt)}</dd>
              </div>
            </dl>
          </Card>

          {can('org.delete') ? (
            <Card>
              <CardHeader title="危险操作" hint="解散后不可恢复" />
              <Notice tone="danger">
                解散组织会删除它的成员关系、邀请、接入应用与组织设置，并让全部成员在该组织的会话立即失效。
                账号本身会保留。
              </Notice>
              <Button variant="danger" className="mt-4" onClick={() => setConfirmDelete(true)}>
                解散组织
              </Button>
            </Card>
          ) : null}
        </div>
      </div>

      <ConfirmDialog
        open={confirmDelete}
        title={`解散组织「${org.data?.tenant.name ?? ''}」？`}
        description="此操作不可恢复。成员关系、邀请、接入应用与组织设置都会被删除。"
        confirmLabel="确认解散"
        onCancel={() => setConfirmDelete(false)}
        onConfirm={async () => {
          setConfirmDelete(false);
          try {
            await api.delete('/api/org');
            toast.push('组织已解散', 'ok');
            // 当前会话所属组织已不存在，回到登录页重新选择。
            await refresh().catch(() => undefined);
            navigate('/login', { replace: true });
          } catch (error) {
            toast.push(describeError(error), 'danger');
          }
        }}
      />
      <Modal
        open={upgradeOpen}
        title="升级为企业组织？"
        description="组织类型变更属于主体性质变更，请确认以下影响后再继续。"
        size="md"
        onClose={() => {
          if (!busy) setUpgradeOpen(false);
        }}
        footer={
          <>
            {totpEnabled ? null : (
              <Button
                onClick={() => {
                  setUpgradeOpen(false);
                  navigate('/security');
                }}
              >
                前往启用两步验证
              </Button>
            )}
            <Button onClick={() => setUpgradeOpen(false)} disabled={busy}>
              取消
            </Button>
            <Button
              variant="primary"
              loading={busy}
              disabled={!totpEnabled || upgradeCode.trim().length !== 6}
              onClick={() => void save(upgradeCode.trim())}
            >
              确认升级
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <Notice tone="danger">
            升级后组织会<strong>立即变为「未认证企业」</strong>，需要在「企业资质」页提交资料并通过审核，
            才能恢复邀请成员、生成 API Key 与申请发票等企业能力。
          </Notice>
          <Notice tone="warn">企业组织无法再变更回个人组织，请确认这是一次不可逆的主体变更。</Notice>
          <Notice tone="info">
            如果您是被企业邀请加入的成员，请联系企业获取加入方式，无需自行升级个人空间。
          </Notice>

          {totpEnabled ? (
            <Field
              label="两步验证码"
              htmlFor="upgrade-totp"
              required
              error={upgradeError ?? undefined}
              hint="请输入验证器中当前 6 位数字验证码，用于确认本次变更由本人发起。"
            >
              <Input
                id="upgrade-totp"
                value={upgradeCode}
                onChange={setUpgradeCode}
                maxLength={6}
                placeholder="6 位验证码"
                autoComplete="one-time-code"
              />
            </Field>
          ) : (
            <Notice tone="warn">
              升级为企业组织需要两步验证。请先在「安全」页启用两步验证后再回来操作。
            </Notice>
          )}
        </div>
      </Modal>
    </div>
  );
}
